import { AlertTriangle, BellOff, RefreshCw } from 'lucide-react';
import { useRef, type ReactElement } from 'react';
import { useNavigate } from 'react-router';

import { cn } from '@/components/ui/cn';
import { InlineError, StatePanel } from '@/components/ui/StatePanel';
import { useToast } from '@/components/ui/toast';
import { useBackState } from '@/lib/back-nav';

import { dismissErrorCopy, markAllErrorCopy, markedAllCopy } from '../presentation';
import type { InboxItem as Item } from '../types';
import {
  useDismiss,
  useInbox,
  useMarkAllRead,
  useMarkRead,
  useUnreadCount,
  type InboxFilter,
} from '../use-notifications';

import { InboxItem } from './InboxItem';
import { InboxSkeleton } from './NotificationsSkeleton';
import { errorAdvice, refreshAdvice } from '@/lib/error-advice';

const FILTERS: { value: InboxFilter; label: string }[] = [
  { value: 'abiertos', label: 'Abiertos' },
  { value: 'todos', label: 'Todos' },
];

interface Props {
  companyId: string;
  filter: InboxFilter;
  onFilter: (f: InboxFilter) => void;
}

/** Bandeja de avisos del usuario en la empresa activa (so=370, backend so=330). */
export function Inbox({ companyId, filter, onFilter }: Props): ReactElement {
  const query = useInbox(filter, { companyId, enabled: true });
  const markRead = useMarkRead();
  const dismiss = useDismiss();
  const navigate = useNavigate();
  const fromHere = useBackState('Notificaciones');
  const { showToast } = useToast();
  const markAll = useMarkAllRead();
  const unread = useUnreadCount({ companyId, enabled: true }).data ?? 0;
  const busy = markRead.isPending || dismiss.isPending || markAll.isPending;
  const items = query.data?.pages.flatMap((p) => p.items) ?? [];

  const open = (item: Item, path: string): void => {
    if (item.readAt === null) {
      markRead.mutate(item.id);
    }
    void navigate(path, { state: fromHere });
  };
  const read = (id: string): void =>
    markRead.mutate(id, { onError: (e) => showToast(dismissErrorCopy(e), 'error') });
  // Al terminar el botón desaparece (no quedan no leídos): el foco vuelve al título
  // de la bandeja en vez de perderse en <body>.
  const titleRef = useRef<HTMLHeadingElement>(null);
  const readAll = (): void =>
    markAll.mutate(undefined, {
      onSuccess: (n) => {
        showToast(markedAllCopy(n));
        titleRef.current?.focus();
      },
      onError: (e) => showToast(markAllErrorCopy(e), 'error'),
    });
  const discard = (id: string): void =>
    dismiss.mutate(id, {
      onSuccess: () => showToast('Aviso descartado'),
      onError: (e) => showToast(dismissErrorCopy(e), 'error'),
    });

  // Una recarga o una página siguiente que falla conserva lo que ya se veía y lo avisa; con
  // la falla de "Cargar más" el reintento es este, sin el botón duplicado.
  const refreshFailure = query.isError ? (
    <InlineError
      icon={<RefreshCw size={16} />}
      onRetry={() => void (query.isFetchNextPageError ? query.fetchNextPage() : query.refetch())}
      retrying={query.isFetching}
    >
      {query.isFetchNextPageError
        ? `No se pudieron cargar más avisos. ${errorAdvice(query.error)}`
        : items.length === 0
          ? `No se pudo comprobar si hay avisos nuevos.${refreshAdvice(query.error)}`
          : `No se pudieron actualizar los avisos. Se muestran los anteriores.${refreshAdvice(query.error)}`}
    </InlineError>
  ) : null;

  return (
    <div className="card rise d1">
      <div className="ch">
        <h3 ref={titleRef} tabIndex={-1}>
          Mis avisos
        </h3>
        <div className="inbox-tools">
          {(unread > 0 || items.some((i) => i.readAt === null)) && (
            <button type="button" className="btn mut sm" disabled={busy} onClick={readAll}>
              Marcar todos como leídos
            </button>
          )}
          <div className="seg" role="group" aria-label="Filtrar avisos">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                className={cn(filter === f.value && 'on')}
                aria-pressed={filter === f.value}
                onClick={() => onFilter(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      {query.isLoading ? (
        <InboxSkeleton />
      ) : query.isError && query.data === undefined ? (
        <div className="cb">
          <StatePanel
            icon={<AlertTriangle />}
            title="No se pudieron cargar los avisos"
            tone="danger"
            role="alert"
            onRetry={() => void query.refetch()}
            retrying={query.isRefetching}
          >
            {errorAdvice(query.error)}
          </StatePanel>
        </div>
      ) : items.length === 0 ? (
        <>
          <div className="cb nempty" role="status">
            <BellOff aria-hidden="true" />
            <p>{filter === 'abiertos' ? 'No tienes avisos abiertos' : 'No tienes avisos'}</p>
          </div>
          {refreshFailure}
        </>
      ) : (
        <>
          <ul className="notices" aria-label="Avisos">
            {items.map((item) => (
              <InboxItem
                key={item.id}
                item={item}
                busy={busy}
                onOpen={open}
                onRead={read}
                onDismiss={discard}
              />
            ))}
          </ul>
          {query.hasNextPage && !query.isFetchNextPageError && (
            <div className="cb nmore">
              <button
                type="button"
                className="btn mut sm"
                disabled={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              >
                {query.isFetchingNextPage ? 'Cargando…' : 'Cargar más'}
              </button>
            </div>
          )}
          {refreshFailure}
        </>
      )}
    </div>
  );
}
